<template>
  <div>
    <div>
      <o-dropdown
        ref="dropdown"
        class="w-full"
        :arrow="false"
        placement="bottom-start"
        detect-overflow
        same-width
        :disabled="disabled"
        :trigger="[]"
        :clickaway="false"
      >
        <o-field-group :label="$t('general.addressLine1')" class="mb-4 w-full">
          <text-field
            v-model="_value.address"
            leading="address"
            autocomplete="new-password"
            :loading="loading"
            @input="fetchAddresses"
            @blur="handleInputBlur"
            @focus="handleInputFocus"
            @keyup.up="handleInputKeyUp(-1)"
            @keyup.down="handleInputKeyUp(1)"
            @keyup.enter="handleInputEnter"
          />
        </o-field-group>

        <template #content="{ overflow }">
          <div
            class="text-sm flex flex-col"
            :style="{
              maxHeight: overflow && overflow.bottom > 0 ? `${400 - overflow.bottom}px` : '400px'
            }"
          >
            <o-scroll v-if="autocompleteAddresses.length">
              <ul
                v-for="(address, index) in autocompleteAddresses"
                :key="index"
              >
                <li
                  :class="[
                    'cursor-pointer select-none relative py-2 px-3  transition-colors border-b last:border-none',
                    focused === index ? 'bg-blue-100 text-blue-700 dark:bg-blue-800/20 dark:text-blue-500' : 'text-gray-900 dark:text-gray-50'
                  ]"
                  role="option"
                  @click="setAddress(address)"
                  @mouseenter="focused = index"
                >
                  {{ address.formattedAddress }}
                </li>
              </ul>
            </o-scroll>

            <p v-else class="text-center px-4 py-2 text-gray-600">
              {{ $t('components.elements.address.noMatchingAddress') }}
            </p>
          </div>
        </template>
      </o-dropdown>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full" :label="$t('general.addressLine2')">
          <text-field v-model="_value.address2" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.suburb')">
          <text-field v-model="_value.suburb" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.city')">
          <text-field v-model="_value.city" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.region')">
          <text-field v-model="_value.region" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.postcode')">
          <text-field v-model="_value.postcode" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.country')">
          <text-field v-model="_value.country" />
        </o-field-group>
      </div>
    </div>

    <div id="address-autocomplete-tag" />
  </div>
</template>

<script lang="ts">
export default { name: 'OAddress' }
</script>

<script setup lang="ts">
import Radar from 'radar-sdk-js'
import debounce from 'lodash/debounce'

import {
  TextField,
} from '@/components/fields'

const radar_test_key = 'prj_test_pk_7d0b8d3107d27df9fb890c5141faeb27f218a146'
const radar_live_key = 'prj_test_pk_7d0b8d3107d27df9fb890c5141faeb27f218a146'

const instance = getCurrentInstance()

interface Props {
  modelValue: Record<string, any>
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void
}>()

const loading = ref(false)
const result = ref<any>(null)
const focused = ref(-1)
const dropdown = ref<any>()
const autocompleteAddresses = computed(() => result.value?.addresses || [])

const _value = computed({
  get() {
    return props.modelValue
  },
  set(value: Record<string, any>) {
    emit('update:modelValue', value)
  },
})

const isDev = computed(() => {
  return instance?.proxy?.$config.public.instance === 'dev'
})

onMounted(() => {
  Radar.initialize(isDev.value ? radar_test_key : radar_live_key)
})

const fetchAddresses = debounce(async function () {
  loading.value = true

  try {
    const res = await Radar.autocomplete({
      query: _value.value.address,
      limit: 5,
    })

    result.value = res
    focused.value = -1

    dropdown.value?.open()
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}, 500)

function setAddress(res: any) {
  _value.value.address = res.addressLabel
  _value.value.address2 = ''
  _value.value.suburb = res.dependentLocality || res.borough || ''
  _value.value.city = res.city
  _value.value.region = res.state
  _value.value.postcode = res.postalCode
  _value.value.country = res.country
}

function handleInputBlur() {
  setTimeout(() => {
    dropdown.value?.close()
  }, 200)
}

function handleInputFocus() {
  if (autocompleteAddresses.value.length) {
    dropdown.value?.open()
  }
}

function handleInputKeyUp(val: number) {
  const addresses = autocompleteAddresses.value

  if (addresses.length) {
    const next = focused.value + val

    if (next > -1 && next < addresses.length) {
      focused.value = next
    }
  }
}

function handleInputEnter() {
  const addresses = autocompleteAddresses.value

  if (addresses.length) {
    const address = addresses[focused.value]

    if (address) {
      setAddress(address)
      dropdown.value?.close()
    }
  }
}
</script>
